import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Form, Modal, Alert } from 'react-bootstrap';
import { getKYCDetails, processKYC, deleteKYC } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const KYCDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [kycDetails, setKycDetails] = useState(null);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [remarks, setRemarks] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // State for image preview modal
    const [showImageModal, setShowImageModal] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');

    const handleImagePreview = (imageUrl, title) => {
        if (!imageUrl) {
            alert('Image not available');
            return;
        }
        // Construct full image URL - adjust based on your API base URL
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${baseUrl}/${imageUrl}`;
        setPreviewImage(fullImageUrl);
        setPreviewTitle(title);
        setShowImageModal(true);
    };

    useEffect(() => {
        if (!id) {
            setError('KYC ID is required');
            setTimeout(() => navigate('/admin/kyc-list'), 2000);
            return;
        }
        fetchKYCDetails();
    }, [id]);

    const fetchKYCDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getKYCDetails(id);
            
            if (response.success) {
                setKycDetails(response.data);
            } else {
                throw new Error(response.message || 'Failed to fetch KYC details');
            }
        } catch (error) {
            console.error('Error fetching KYC details:', error);
            setError(error.message || 'Failed to fetch KYC details');
        } finally {
            setLoading(false);
        }
    };

    const handleProcess = async (status) => {
        if (status === 'rejected' && !remarks.trim()) {
            alert('Remarks are required for rejection');
            return;
        }

        try {
            setProcessing(true);
            const response = await processKYC(id, status, remarks);

            if (response.success) {
                alert(`KYC application ${status} successfully`);
                setRemarks('');
                fetchKYCDetails(); // Refresh the details
            } else {
                throw new Error(response.message || `Failed to ${status} KYC application`);
            }
        } catch (error) {
            console.error('Error processing KYC:', error);
            alert(error.message || `Failed to ${status} KYC application`);
        } finally {
            setProcessing(false);
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            const response = await deleteKYC(id);
            
            if (response.success) {
                alert('KYC record deleted successfully');
                navigate('/admin/kyc-list');
            } else {
                throw new Error(response.message || 'Failed to delete KYC record');
            }
        } catch (error) {
            console.error('Error deleting KYC record:', error);
            alert(error.message || 'Failed to delete KYC record');
        } finally {
            setDeleting(false);
            setShowDeleteModal(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: 'warning',
            approved: 'success',
            rejected: 'danger'
        };
        return badges[status] || 'secondary';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="container-xxl">
                <PageTitle pagetitle="KYC Details" sidebutton={true} />
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted">Loading KYC details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !kycDetails) {
        return (
            <div className="container-xxl">
                <PageTitle pagetitle="KYC Details" sidebutton={true} />
                <Alert variant="danger">
                    <i className="fa fa-exclamation-circle me-2"></i>
                    {error}
                </Alert>
            </div>
        );
    }

    if (!kycDetails) {
        return (
            <div className="container-xxl">
                <PageTitle pagetitle="KYC Details" sidebutton={true} />
                <Alert variant="info">
                    <i className="fa fa-info-circle me-2"></i>
                    No KYC details found
                </Alert>
            </div>
        );
    }

    const user = kycDetails.userId || kycDetails.user || {};

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="KYC Details" sidebutton={true} />

            {/* Image Preview Modal */}
            <Modal
                show={showImageModal}
                onHide={() => setShowImageModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{previewTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <img
                        src={previewImage}
                        alt={previewTitle}
                        style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIGZpbGw9IiNFNUU1RTUiLz48L3N2Zz4=';
                        }}
                    />
                </Modal.Body>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this KYC record? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className="row mb-3">
                <div className="col-12">
                    <div className="d-flex justify-content-between">
                        <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                            <i className="fa fa-trash me-2"></i>Delete KYC
                        </Button>
                        <Link to="/admin/kyc-list" className="btn btn-outline-secondary">
                            <i className="fa fa-arrow-left me-2"></i>Back to List
                        </Link>
                    </div>
                </div>
            </div>

            {error && (
                <Alert variant="danger" className="mb-3">
                    <i className="fa fa-exclamation-circle me-2"></i>
                    {error}
                </Alert>
            )}

            <Row>
                <Col xl={6} lg={6}>
                    <Card className="border-0 shadow-sm mb-3">
                        <Card.Header className="bg-white border-bottom">
                            <Card.Title className="mb-0">User Information</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="table-responsive">
                                <table className="table table-borderless mb-0">
                                    <tbody>
                                        <tr>
                                            <td className="fw-semibold">Name</td>
                                            <td className="text-end">{user.name || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-semibold">Email</td>
                                            <td className="text-end">{user.email || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-semibold">Mobile</td>
                                            <td className="text-end">{user.mobileNumber || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-semibold">Status</td>
                                            <td className="text-end">
                                                <Badge bg={getStatusBadge(kycDetails.status)}>
                                                    {kycDetails.status?.toUpperCase() || '-'}
                                                </Badge>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="fw-semibold">Submitted On</td>
                                            <td className="text-end">
                                                {formatDateTime(kycDetails.submittedAt || kycDetails.createdAt)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={6} lg={6}>
                    <Card className="border-0 shadow-sm mb-3">
                        <Card.Header className="bg-white border-bottom">
                            <Card.Title className="mb-0">Document Information</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="table-responsive">
                                <table className="table table-borderless mb-0">
                                    <tbody>
                                        <tr>
                                            <td className="fw-semibold">Document Type</td>
                                            <td className="text-end">
                                                <Badge bg="info">
                                                    {kycDetails.documentType?.toUpperCase() || '-'}
                                                </Badge>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="fw-semibold">Document Number</td>
                                            <td className="text-end">{kycDetails.documentNumber || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-semibold">Document Front</td>
                                            <td className="text-end">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => handleImagePreview(kycDetails.documentFront, 'Document Front')}
                                                    disabled={!kycDetails.documentFront}
                                                >
                                                    <i className="fa fa-image me-1"></i> View Image
                                                </Button>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="fw-semibold">Document Back</td>
                                            <td className="text-end">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => handleImagePreview(kycDetails.documentBack, 'Document Back')}
                                                    disabled={!kycDetails.documentBack}
                                                >
                                                    <i className="fa fa-image me-1"></i> View Image
                                                </Button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={12}>
                    <Card className="border-0 shadow-sm mb-3">
                        <Card.Header className="bg-white border-bottom">
                            <Card.Title className="mb-0">Address Information</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <div className="table-responsive">
                                        <table className="table table-borderless mb-0">
                                            <tbody>
                                                <tr>
                                                    <td className="fw-semibold">Full Name</td>
                                                    <td className="text-end">{kycDetails.fullName || '-'}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">Date of Birth</td>
                                                    <td className="text-end">
                                                        {kycDetails.dateOfBirth ? formatDate(kycDetails.dateOfBirth) : '-'}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">Address</td>
                                                    <td className="text-end">{kycDetails.address || '-'}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="table-responsive">
                                        <table className="table table-borderless mb-0">
                                            <tbody>
                                                <tr>
                                                    <td className="fw-semibold">City</td>
                                                    <td className="text-end">{kycDetails.city || '-'}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">State</td>
                                                    <td className="text-end">{kycDetails.state || '-'}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">Country</td>
                                                    <td className="text-end">{kycDetails.country || '-'}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">Pincode</td>
                                                    <td className="text-end">{kycDetails.pincode || '-'}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {kycDetails.status === 'pending' && (
                    <Col xl={12}>
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom">
                                <Card.Title className="mb-0">Process Application</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Remarks</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        placeholder="Enter remarks (required for rejection)"
                                    />
                                    <Form.Text className="text-muted">
                                        Remarks are required when rejecting an application
                                    </Form.Text>
                                </Form.Group>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="success"
                                        disabled={processing}
                                        onClick={() => handleProcess('approved')}
                                    >
                                        {processing ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-check me-2"></i> Approve
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="danger"
                                        disabled={processing}
                                        onClick={() => handleProcess('rejected')}
                                    >
                                        {processing ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-times me-2"></i> Reject
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                )}

                {kycDetails.remarks && (
                    <Col xl={12}>
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom">
                                <Card.Title className="mb-0">Remarks</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <p className="mb-0">{kycDetails.remarks}</p>
                            </Card.Body>
                        </Card>
                    </Col>
                )}
            </Row>
        </div>
    );
};

export default KYCDetails;

